import { useContext } from "@nuxtjs/composition-api";
import { useZoneActions } from "~/components/_refactored/conductor/zones/useZoneActions";
import { useDataTableFilterOperators } from "~/components/_refactored/conductor/data-table/useDataTableFilterOperators";
import { useDataTableQuery } from "~/components/_refactored/conductor/data-table/useDataTableQuery";

export function useMiniMarketActions() {
  const { $mutate, $query, i18n, $joszaki } = useContext();

  const zoneActions = useZoneActions();
  const OPERATORS = useDataTableFilterOperators();

  async function saveMiniMarket(submitData) {
    try {
      if (submitData.id !== null) {
        const UPDATE_MINI_MARKET = await import("./updateMiniMarket.graphql");
        return await $mutate(UPDATE_MINI_MARKET, submitData, {
          showErrorToast: true,
          showSuccessToast: true,
          successMessage: i18n.t("conductor.messages.updateSuccess", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
          errorMessage: i18n.t("conductor.messages.updateError", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
        });
      } else {
        const CREATE_MINI_MARKET = await import("./createMiniMarket.graphql");
        return await $mutate(CREATE_MINI_MARKET, submitData, {
          showErrorToast: true,
          showSuccessToast: true,
          successMessage: i18n.t("conductor.messages.createSuccess", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
          errorMessage: i18n.t("conductor.messages.createError", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
        });
      }
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function deleteMiniMarketById(id) {
    try {
      const DELETE_MINI_MARKET = await import("./deleteMiniMarketById.graphql");
      await $mutate(
        DELETE_MINI_MARKET,
        { id },
        {
          showErrorToast: true,
          showSuccessToast: true,
          successMessage: i18n.t("conductor.messages.deleteSuccess", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
          errorMessage: i18n.t("conductor.messages.deleteError", {
            entity: i18n.t("conductor.miniMarkets.singular"),
          }),
        }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getMiniMarketById(id) {
    try {
      const GET_MINI_MARKET = await import("./getMiniMarketById.graphql");
      const { miniMarketById } = await $query(GET_MINI_MARKET, { id });
      return miniMarketById;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  const getMiniMarkets = useDataTableQuery(
    () => import("./miniMarketsDataTable.graphql"),
    {
      mapItem: (m) => {
        const tenderValue = m.income / m.averageMonthlyUniqueTenderCount;
        const targetIncomeByTenderCount =
          m.averageMonthlyUniqueTenderCount * m.targetTenderPrice;
        const missingGrowthToTargetIncome =
          m.averageMonthlyUniqueTenderCount * m.targetTenderPrice - m.income;

        const actualFreeCapacity =
          (m.actualCapacity ?? 0) - m.activeSubscriptionCount;

        return {
          ...m,
          tenderValue,
          targetIncomeByTenderCount,
          missingGrowthToTargetIncome,
          actualFreeCapacity,
        };
      },
    }
  );

  async function getMiniMarketStatistics() {
    try {
      const GET_MINI_MARKET_STATS = await import(
        "./miniMarketStatistics.graphql"
      );
      const { miniMarketStatistics } = await $query(
        GET_MINI_MARKET_STATS,
        {},
        {
          showErrorToast: true,
          errorMessage: i18n.t("conductor.miniMarkets.stats.error"),
        }
      );
      return miniMarketStatistics;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function mergeMiniMarkets(sourceMiniMarketId, targetMiniMarketId) {
    try {
      const MERGE_MINI_MARKETS = await import("./mergeMiniMarkets.graphql");
      return await $mutate(
        MERGE_MINI_MARKETS,
        { sourceMiniMarketId, targetMiniMarketId },
        {
          showErrorToast: true,
          showSuccessToast: true,
        }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function cloneZones(
    sourceMiniMarketId,
    sourceVersionId,
    targetMiniMarketId,
    targetVersionId,
    copyCapacityHistory
  ) {
    try {
      const { items: targetZones } = await zoneActions.getZones({
        page: 1,
        pageSize: 1000,
        filters: [
          {
            filterId: "miniMarketId",
            value: targetMiniMarketId,
            operator: OPERATORS.EQUALS,
          },
          {
            filterId: "miniMarketZoneVersionId",
            value: targetVersionId,
            operator: OPERATORS.EQUALS,
          },
        ],
      });

      if (targetZones.length) {
        $joszaki.toast({
          message: i18n.t(
            "conductor.miniMarkets.actions.clone.targetVersionHasZones"
          ),
          type: "error",
        });
        return;
      }

      const { items: sourceZones } = await zoneActions.getZones({
        page: 1,
        pageSize: 1000,
        filters: [
          {
            filterId: "miniMarketId",
            operator: OPERATORS.EQUALS,
            value: sourceMiniMarketId,
          },
          {
            filterId: "miniMarketZoneVersionId",
            operator: OPERATORS.EQUALS,
            value: sourceVersionId,
          },
        ],
      });

      for (const zone of sourceZones) {
        const sourceZone = await zoneActions.getZoneById(zone.id);
        const capacities = copyCapacityHistory
          ? sourceZone.capacities.map((item) => ({
              capacity: item.capacity,
              actualCapacity: item.actualCapacity,
              tenderPerPerson: item.tenderPerPerson,
              note: item.note,
              validFrom: new Date().toISOString(),
              validUntil: null,
            }))
          : [
              {
                capacity: 1,
                actualCapacity: null,
                tenderPerPerson: sourceZone.tenderPerPerson,
                note: "",
                validFrom: new Date().toISOString(),
                validUntil: null,
              },
            ];

        await zoneActions.saveZone({
          name: sourceZone.name,
          price: sourceZone.price,
          tenderPerPerson: sourceZone.tenderPerPerson,
          area: sourceZone.area,
          waitingList: sourceZone.waitingList,
          miniMarketId: targetMiniMarketId,
          versionId: targetVersionId,
          note: sourceZone.note,
          subcountyIds: sourceZone.subcounties.map((s) => s.id),
          capacities,
        });
      }
    } catch (e) {
      $joszaki.toast({
        message: i18n.t("conductor.miniMarkets.actions.clone.failedToClone"),
        type: "error",
      });
      throw e;
    }
  }

  async function migrateProfessionals(
    miniMarketId,
    fromVersionId,
    toVersionId
  ) {
    try {
      const MIGRATE_PROFESSIONALS = await import(
        "./migrateProfessionalsBetweenZoneVersions.graphql"
      );
      const result = await $mutate(
        MIGRATE_PROFESSIONALS,
        {
          miniMarketId,
          fromVersionId,
          toVersionId,
        },
        {
          showErrorToast: true,
          errorMessage: i18n.t(
            "conductor.miniMarkets.actions.migrate.failedToMigrate"
          ),
        }
      );
      return result;
    } catch (e) {
      console.error("Failed to migrate professionals:", e);
      throw e;
    }
  }

  return {
    saveMiniMarket,
    deleteMiniMarketById,
    getMiniMarketById,
    getMiniMarkets,
    getMiniMarketStatistics,
    mergeMiniMarkets,
    cloneZones,
    migrateProfessionals,
  };
}
